import { CreateType, Event, InvitationStatus, FileType, Prisma } from "@prisma/client";
import { EventFormQuestionAnswerType } from "./event-form-question.types";
import { UserType } from "./user.types";
import { Paginated, PaginatedResponse, Sortable } from "./utils.types";

// An unsafe visitor is a visitor that contains user's unshared informations
export const unsafeVisitorValues = Prisma.validator<Prisma.VisitorDefaultArgs>()({
  include: {
    user: {
      include: {
        picture: true,
        userSharedInformation: true,
      },
    },
    groups: true,
  },
});

export const getUnsafeVisitorValuesFromStructure = (structureId: string) =>
  Prisma.validator<Prisma.VisitorDefaultArgs>()({
    include: {
      ...unsafeVisitorValues.include,
      user: {
        ...unsafeVisitorValues.include.user,
        include: {
          ...unsafeVisitorValues.include.user.include,
          scannedStructures: {
            where: {
              structureId,
            },
            select: {
              event: {
                select: {
                  id: true,
                  name: true,
                },
              },
            },
            orderBy: {
              createdAt: "asc",
            },
          },
        },
      },
    },
  });

type BaseUnsafeVisitor = Prisma.VisitorGetPayload<typeof unsafeVisitorValues>;

type UnsafeVisitorFromStructure = Prisma.VisitorGetPayload<
  ReturnType<typeof getUnsafeVisitorValuesFromStructure>
>;

export type UnsafeVisitor = BaseUnsafeVisitor | UnsafeVisitorFromStructure;

type ExtraVisitorProps = Pick<
  UserType,
  "pictureId" | "picture" | "isActive" | "deletedAt" | "interest" | "siret" | "vat"
> & { firstEventScanned: Pick<Event, "name" | "id"> | null };

export type Visitor = Omit<UnsafeVisitor, "user"> & ExtraVisitorProps;

// An unsafe event visitor is an event visitor that contains user's unshared informations
export const getUnsafeEventVisitorValues = (eventId: string) =>
  Prisma.validator<Prisma.VisitorDefaultArgs>()({
    ...unsafeVisitorValues,
    include: {
      ...unsafeVisitorValues.include,
      givenBadges: {
        where: {
          badgeType: {
            eventId,
          },
        },
      },
      givenTickets: {
        where: {
          ticketType: {
            eventId,
          },
        },
      },
    },
  });

const unsafeEventVisitorValues = Prisma.validator<Prisma.VisitorDefaultArgs>()({
  ...unsafeVisitorValues,
  include: {
    ...unsafeVisitorValues.include,
    givenBadges: true,
    givenTickets: true,
  },
});

export type UnsafeEventVisitor = Prisma.VisitorGetPayload<typeof unsafeEventVisitorValues>;

export type EventVisitor = Omit<UnsafeEventVisitor, "user"> & ExtraVisitorProps;

export const createTypeTranslation: Record<CreateType, string> = {
  manual: "Saisie",
  import: "Importé",
  scan: "Scanné",
  request: "Demande spontanée",
  transfer: "Par transfert",
};

export const invitationStatusTranslation: Record<InvitationStatus, string> = {
  sent: "Envoyée",
  waiting: "En attente",
  opened: "Acceptée",
  refused: "Refusée",
};

export type VisitorType = CreateType | "dataDeleted" | "deleted";

export const visitorTypeTranslation: Record<VisitorType, string> = {
  dataDeleted: "Données supprimées",
  deleted: "Compte Supprimé",
  ...createTypeTranslation,
};

export const getVisitorEventGivenTicketsAndBadges = (eventId?: string) =>
  Prisma.validator<Prisma.VisitorDefaultArgs>()({
    include: {
      ...unsafeVisitorValues.include,
      givenBadges: {
        where: {
          badgeType: {
            eventId,
          },
        },
        include: {
          badgeType: true,
        },
      },
      givenTickets: {
        where: {
          ticketType: {
            eventId,
          },
        },
        include: {
          ticketType: true,
        },
      },
    },
  });

export const visitorEventGivenTicketsAndBadgesArgs = Prisma.validator<Prisma.VisitorDefaultArgs>()({
  include: {
    givenBadges: {
      where: {
        badgeType: {
          eventId: undefined, // undefined as we cannot give dynamic variable here
        },
      },
      include: {
        badgeType: true,
      },
    },
    givenTickets: {
      where: {
        ticketType: {
          eventId: undefined, // same as above
        },
      },
      include: {
        ticketType: true,
      },
    },
  },
});

export type VisitorEventGivenTicketsAndBadges = Prisma.VisitorGetPayload<
  typeof visitorEventGivenTicketsAndBadgesArgs
>;

export type BusinessSectorInterest = {
  id: string;
  name: string;
};

export type ProEventVisitorsFilter = Sortable<
  Paginated<{ searchText?: string | null; qualifiedOnly?: boolean | null }>
>;

export type ProEventAttendee = {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  dateOfBirth: Date | null;
  phoneNumber: string | null;
  zipCode: string | null;
  picture: { key: string; name: string; type: FileType } | null;
  eventInterests: BusinessSectorInterest[];
  isEntryNotificationSubscribed: boolean;
  isEmailNotified: boolean;
  canReceiveExhibitorEmail: boolean;
  formAnswers: Record<string, { answer: string | null; answerList: string[] }>;
};

export type ProEventFormQuestion = {
  questionId: string;
  theme: string;
  question: string;
  answerType: EventFormQuestionAnswerType;
  options: { id: string; label: string }[];
};

export type ProEventVisitorsResponse = PaginatedResponse<ProEventAttendee> & {
  entryNotificationSubscriptionCount: number;
  maxEntryNotifications: number;
  isMobileNotificationEnabled: boolean;
  formQuestions: ProEventFormQuestion[];
};
